<template>
  <div :class="$style.container">
    <SCAWorkspaceItem
      :title="org.name"
      :subtitle="label"
      :icon
      :selected="isSelected"
      :expandable="hasChildren"
      :expanded="expanded"
      :level="level"
      @toggle-expand="toggle"
      @click="select"
    />
    <Transition
      name="expand"
      @before-enter="beforeEnter"
      @enter="enter"
      @after-enter="afterEnter"
      @before-leave="beforeLeave"
      @leave="leave"
      @after-leave="afterLeave"
    >
      <div v-if="org.children.length > 0 && expanded" :class="$style.container">
        <OrgTreeItem
          v-for="child in org.children"
          :key="child.id"
          :selected-org="selectedOrg"
          :org="child"
          :level="level + 1"
          :expand-all="expandAll"
          @update:selected-org="$emit('update:selected-org', $event)"
        />
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import type { OrganizationWithChildren } from "@scayla/api-client-fetch";
import { SCAWorkspaceItem } from "@scayla/components";
import { computed, ref } from "vue";

import { useWorkspace } from "@/store/workspace";

import { getIcon, getLabel } from "./tenantOrgaUtils";

const workspace = useWorkspace();
const props = defineProps<{
  org: OrganizationWithChildren;
  level: number;
  expandAll?: boolean;
  selectedOrg?: string;
}>();

const expanded = ref(props.expandAll ?? false);

const hasChildren = computed(() => props.org.children.length > 0);
const isSelected = computed(() => props.org.id === props.selectedOrg);

const emit = defineEmits<{
  "update:selected-org": [id: string | undefined];
}>();

const toggle = () => {
  expanded.value = !expanded.value;
};

const select = () => {
  emit("update:selected-org", props.org.id);
};

const tenant = computed(() => workspace.tenants?.find((t) => t.id === props.org.tenantId));

const label = computed(() => getLabel(false, tenant.value, props.org));
const icon = computed(() => getIcon(false, tenant.value, props.org));

// ------------
// Transition hooks
// ------------

const beforeEnter = (el: Element) => {
  (el as HTMLElement).style.height = "0px";
};

const enter = (el: Element) => {
  (el as HTMLElement).style.height = `${(el as HTMLElement).scrollHeight}px`;
};

const afterEnter = (el: Element) => {
  (el as HTMLElement).style.height = "";
};

const beforeLeave = (el: Element) => {
  (el as HTMLElement).style.height = `${(el as HTMLElement).scrollHeight}px`;
};

const leave = (el: Element) => {
  (el as HTMLElement).style.height = "0px";
};

const afterLeave = (el: Element) => {
  (el as HTMLElement).style.height = "";
};
</script>

<style module lang="scss">
.container {
  display: flex;
  flex-direction: column;
  gap: var(--space-component-gap-xSmall);
}

:global(.expand-enter-active),
:global(.expand-leave-active) {
  transition: height 0.2s linear;
  overflow: hidden;
}

:global(.expand-enter-from),
:global(.expand-leave-to) {
  height: 0;
}
</style>
